import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AuthShell, TextInput, toast } from '@etherisc/ui-kit';
import { PrimaryButton } from '../../components/buttons';
import { apiClient } from '../../services/api';
import { MailIcon } from 'lucide-react';
import { MyUbuntuLogo } from '../../assets/MyUbuntuLogo';

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const handleForgotPassword = async (data: ForgotPasswordFormData) => {
    try {
      setLoading(true);
      
      await apiClient.requestPasswordReset(data.email);
      
      setEmailSent(true);
      toast.success('Reset link sent!', {
        description: 'Check your email for password reset instructions.',
      });
    } catch (error: unknown) {
      console.error('Forgot password error:', error);
      toast.error('Failed to send reset email', {
        description: error instanceof Error ? error.message : 'Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <AuthShell logo={<MyUbuntuLogo className="h-12 w-auto" />}>
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <MailIcon className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">
              Check Your Email
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              We've sent password reset instructions to
            </p>
            <p className="text-sm font-medium text-gray-900">
              {getValues('email')}
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h4 className="text-sm font-medium text-blue-800 mb-2">Next Steps:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Check your email inbox (and spam folder)</li>
                <li>• Click the reset link in the email</li>
                <li>• The link expires in 1 hour for security</li>
                <li>• Contact support if you don't receive the email</li>
              </ul>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setEmailSent(false);
                  setLoading(false);
                }}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Try Different Email
              </button>
              
              <PrimaryButton
                onClick={() => navigate('/login')}
                className="w-full"
              >
                Back to Login
              </PrimaryButton>
            </div>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell logo={<MyUbuntuLogo className="h-12 w-auto" />}>
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900">
            Forgot Password?
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Enter your email address and we'll send you a reset link
          </p>
        </div>

        <form onSubmit={handleSubmit(handleForgotPassword)} className="space-y-6">
          <div>
            <TextInput
              id="email"
              label="Email Address"
              type="email"
              {...register('email')}
              error={errors.email?.message}
              placeholder="Enter your email address"
              autoComplete="email"
            />
          </div>

          <div>
            <PrimaryButton
              type="submit"
              disabled={loading || isSubmitting}
              className="w-full"
            >
              {loading || isSubmitting ? 'Sending Reset Link...' : 'Send Reset Link'}
            </PrimaryButton>
          </div>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Remember your password?</span>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </AuthShell>
  );
}
